import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Copy, ImagePlus, Loader2, X } from 'lucide-react';

const DEPOSIT_PAY_URL = 'https://pay.tide.co/pay/6d065db5-baf2-4969-8d33-1ad5ca43f6a3';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : "Something went wrong locking in your booking. Please WhatsApp us and we'll sort it."; };
const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

export const depositReference = (quoteId) => `MV${(quoteId || '').slice(0, 6).toUpperCase()}`;

// Payment proof the customer gives us: the exact bank reference (required) and an optional screenshot.
export const PaymentProofFields = ({ quoteId, reference, setReference, file, setFile }) => {
  const ours = depositReference(quoteId);
  const copy = () => { navigator.clipboard?.writeText(ours); toast.success('Reference copied'); };
  return (
    <div className="space-y-3">
      <div className="rounded-lg border-2 border-dashed border-[color:var(--mv-orange)] bg-orange-50/60 p-4">
        <div className="text-xs uppercase tracking-wider text-slate-600 font-bold">Use this payment reference</div>
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-2xl font-extrabold tracking-widest text-[color:var(--mv-navy)]" data-testid="payment-expected-ref">{ours}</span>
          <button type="button" onClick={copy} className="h-9 w-9 rounded-md border border-slate-300 bg-white inline-flex items-center justify-center" aria-label="Copy reference" data-testid="copy-ref-btn"><Copy className="w-4 h-4" /></button>
        </div>
      </div>
      <div>
        <label htmlFor="pay-ref-used" className="block text-[11px] font-bold uppercase tracking-wider text-slate-600">Reference you used when paying <span className="text-red-600">*</span></label>
        <input id="pay-ref-used" value={reference} onChange={(e) => setReference(e.target.value)} required minLength={3} maxLength={40} placeholder={ours} className="mt-1 w-full h-11 rounded-md border-2 border-slate-300 focus:border-[color:var(--mv-orange)] bg-white px-3 text-sm font-mono" data-testid="payment-ref-used" />
        <div className="mt-2 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-900" data-testid="payment-ref-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span><strong>This must match exactly what you typed in the reference box in your banking app.</strong> If it doesn’t match, we can’t find your payment and your booking may not go ahead.</span>
        </div>
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Payment screenshot (optional)</div>
        {file ? (
          <div className="mt-1 flex items-center gap-2 text-sm text-[color:var(--mv-navy)]" data-testid="payment-screenshot-chosen">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> <span className="truncate max-w-[220px]">{file.name}</span>
            <button type="button" onClick={() => setFile(null)} aria-label="Remove screenshot" className="h-7 w-7 rounded-md border border-slate-200 inline-flex items-center justify-center" data-testid="payment-screenshot-remove"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <label className="mt-1 h-11 px-3 rounded-md border border-slate-300 bg-white text-sm font-semibold text-[color:var(--mv-navy)] inline-flex items-center gap-2 cursor-pointer" data-testid="payment-screenshot-btn">
            <ImagePlus className="w-4 h-4" /> Add a screenshot
            <input type="file" accept="image/*,.heic,.heif" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="payment-screenshot-input" />
          </label>
        )}
      </div>
    </div>
  );
};

export const submitPaymentProof = async (quoteId, reference, file) => {
  if (file && file.size > 10 * 1024 * 1024) throw Object.assign(new Error('big'), { response: { data: { detail: 'That screenshot is too large (max 10 MB).' } } });
  const screenshot = file ? await readFile(file) : null;
  await axios.post(`${API}/estimate/${quoteId}/deposit-claimed`, { reference: reference.trim(), screenshot });
};

export const DepositTransfer = ({ quoteId, date, time, extras, weekdayDiscount, ready, deposit }) => {
  const navigate = useNavigate();
  const [reference, setReference] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const refOk = reference.trim().length >= 3;

  const book = async () => {
    if (!ready) return toast.error('Please pick and check your date & time in Step 1 above.');
    if (!refOk) return toast.error('Enter the exact reference you used when paying.');
    setBusy(true);
    try {
      const { data: slot } = await axios.post(`${API}/estimate/check-slot`, { date, time, quote_id: quoteId });
      if (!slot.available) { toast.error(slot.message); return; }
      await axios.post(`${API}/estimate/${quoteId}/book`, { date, time, extras: extras.map(code => ({ code, label: code, price: 0 })), weekday_discount: weekdayDiscount });
      try { await submitPaymentProof(quoteId, reference, file); }
      catch (e) {
        try {
          if (!file) throw e;
          await submitPaymentProof(quoteId, reference, null);
          toast.error("You're booked, but the screenshot didn't upload. Your reference was saved.");
        } catch (_) {
          toast.error(`You're booked, but we couldn't save your payment reference. Please WhatsApp it to us on 07985 291097.`);
        }
      }
      navigate(`/booked?quote=${quoteId}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-3" data-testid="deposit-transfer">
      <a href={DEPOSIT_PAY_URL} target="_blank" rel="noreferrer" className="btn-primary text-sm w-full justify-center" data-testid="pay-deposit-btn">Pay £{deposit} deposit via Tide →</a>
      <PaymentProofFields quoteId={quoteId} reference={reference} setReference={setReference} file={file} setFile={setFile} />
      <button type="button" onClick={book} disabled={busy || !ready || !refOk} data-testid="ive-paid-btn" className="w-full bg-[color:var(--mv-navy)] hover:bg-[#13315c] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm py-3 rounded-md inline-flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Locking in your booking…</>
          : !ready ? <><CheckCircle2 className="w-4 h-4" /> Pick a date & time in Step 1 first</>
          : !refOk ? <><CheckCircle2 className="w-4 h-4" /> Enter your payment reference first</>
          : <><CheckCircle2 className="w-4 h-4" /> I’ve paid the deposit — lock in my booking</>}
      </button>
      <p className="text-xs text-slate-600 leading-relaxed">We check every deposit against your reference in our bank account before the day.</p>
    </div>
  );
};
